import {hasQuotePermission, isQuotePermission} from '../../../../utils/quote-access';
import {Component, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DashboardService} from '../../../../services/dashboard.service';
import {Router} from '@angular/router';
import {SessionService} from '../../../../services/session.service';
import {Permissions} from '../../../../enums/permissions';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

interface DashboardCard {
  iconSvg: SafeHtml;
  title: string;
  mainValue: number | string;
  chartData?: { value: number; name: string }[];
  entityName?: string;
  isEmpty?: boolean;
  clickable?: boolean;
  action?: () => void;
}

@Component({
  selector: 'dashboard-charts',
  templateUrl: './dashboard-charts.component.html',
  styleUrls: ['./dashboard-charts.component.css'],
  standalone: false
})
export class DashboardChartsComponent {
  data: any;
  currentUser: any;
  quoteData!: number;
  serviceRequestData!: number;
  assetsData!: DashboardCard['chartData'];
  jobsData!: DashboardCard['chartData'];

  readonly entityName_Assets = 'Asset';
  readonly entityName_Jobs = 'Job';

  pendingIcon: SafeHtml;
  assetsIcon: SafeHtml;
  jobsIcon: SafeHtml;
  serviceRequestsIcon: SafeHtml;

  cards: DashboardCard[] = [];

  protected readonly Permissions = Permissions;
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);

    this.pendingIcon = this.sanitizer.bypassSecurityTrustHtml(`
     <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" height="30px" width="30px"
                   class="w-full p-1 text-white">
                <path
                  d="M14 3.75a.75.75 0 00-.22-.53l-3-3a.75.75 0 00-.53-.22h-9A1.25 1.25 0 000 1.25v11.5A1.25 1.25 0 001.25 14h11.5A1.25 1.25 0 0014 12.75zM1.75 12.5a.25.25 0 01-.25-.25V1.75a.25.25 0 01.25-.25h7.5a.25.25 0 01.25.25v2a.76.76 0 00.75.75h2a.25.25 0 01.25.25v7.5a.25.25 0 01-.25.25z"></path>
                <path
                  d="M5 5.25a.76.76 0 01.63.31.75.75 0 101.24-.84 2.16 2.16 0 00-1-.79.25.25 0 01-.15-.23v-.2a.75.75 0 00-1.5 0v.24a.24.24 0 01-.11.26A2 2 0 003 5.75a1.85 1.85 0 001.11 1.69l1.19.5a.35.35 0 01.2.31.5.5 0 01-.5.5.76.76 0 01-.63-.31.75.75 0 00-1.24.84 2.16 2.16 0 001 .79.25.25 0 01.15.23v.2a.75.75 0 001.5 0v-.24a.24.24 0 01.11-.26A2 2 0 007 8.25a1.85 1.85 0 00-1.11-1.69l-1.19-.5a.35.35 0 01-.2-.31.5.5 0 01.5-.5zM10.25 9h-1.5a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5z"></path>
              </svg>
    `);
    this.assetsIcon = this.sanitizer.bypassSecurityTrustHtml(`
      <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" height="30px" width="30px"
                   class="w-full p-1 text-white">
                <circle cx="7" cy="7" r="3"></circle>
                <path
                  d="M1 5a1 1 0 01-1-1V2a2 2 0 012-2h2a1 1 0 010 2H2.25a.25.25 0 00-.25.25V4a1 1 0 01-1 1zM13 5a1 1 0 01-1-1V2.25a.25.25 0 00-.25-.25H10a1 1 0 010-2h2a2 2 0 012 2v2a1 1 0 01-1 1zM4 14H2a2 2 0 01-2-2v-2a1 1 0 012 0v1.75a.25.25 0 00.25.25H4a1 1 0 010 2zM12 14h-2a1 1 0 010-2h1.75a.25.25 0 00.25-.25V10a1 1 0 012 0v2a2 2 0 01-2 2z"></path>
              </svg>
    `);
    this.jobsIcon = this.sanitizer.bypassSecurityTrustHtml(`
     <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" height="30px" width="30px"
                   class="w-full p-1 text-white">
                <path
                  d="M13.66 2.73a.52.52 0 00-.36-.3.49.49 0 00-.45.14l-2 2.05a1 1 0 01-1.41 0 1 1 0 010-1.42l2-2.05a.5.5 0 00.13-.45.48.48 0 00-.3-.36 4.4 4.4 0 00-5.85 5.49.25.25 0 01-.06.26L.53 10.92a1.8 1.8 0 002.55 2.55l4.83-4.83a.27.27 0 01.26-.06A4.45 4.45 0 0012.11 8a4.42 4.42 0 001.55-5.27z"></path>
              </svg>
    `);
    this.serviceRequestsIcon = this.sanitizer.bypassSecurityTrustHtml(`
     <svg class="w-full p-1 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 25 25"  height="30px" width="30px">
                <path fill-rule="evenodd" d="M9 2.2V7H4.2l.4-.5 3.9-4 .5-.3Zm2-.2v5a2 2 0 0 1-2 2H4v11c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-7ZM8 16c0-.6.4-1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm1-5a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9Z" clip-rule="evenodd"/>
              </svg>
    `);


    if (this.hasPermission(Permissions.ViewDashboards)) {
      this.dashboardService.getDashboard().then((data: any) => {
        this.data = data;
        this.quoteData = data.pending_quotes_count;
        this.serviceRequestData = data.service_request_new_count + data.service_request_in_progress_count;

        const assetsUp = data.assets_upcoming;
        const assetsPd = data.assets_past_due;

        const jobs = [
          data.jobs_received,
          data.jobs_needs_evaluation,
          data.jobs_ready_to_quote,
          data.jobs_awaiting_approval,
          data.jobs_awaiting_parts,
          data.jobs_quote_approved,
          data.jobs_work_in_progress,
          data.jobs_in_cosmetics,
          data.jobs_quote_denied,
          data.jobs_ready_for_shipping
        ];

        this.assetsData = [
          {value: assetsUp, name: 'Upcoming'},
          {value: assetsPd, name: 'Past Due'}
        ];
        this.jobsData = [
          {value: data.jobs_received, name: 'Received'},
          {value: data.jobs_needs_evaluation, name: 'Needs Evaluation'},
          {value: data.jobs_ready_to_quote, name: 'Ready to Quote'},
          {value: data.jobs_awaiting_approval, name: 'Awaiting approval'},
          {value: data.jobs_awaiting_parts, name: 'Awaiting Parts'},
          {value: data.jobs_quote_approved, name: 'Quote Approved'},
          {value: data.jobs_work_in_progress, name: 'Work in Progress'},
          {value: data.jobs_in_cosmetics, name: 'In-Cosmetics'},
          {value: data.jobs_quote_denied, name: 'Quote Denied'},
          {value: data.jobs_ready_for_shipping, name: 'Ready for Shipping'}
        ];

        const cards: DashboardCard[] = [];
        if (this.currentUser.is_owner && this.hasPermission(Permissions.ViewQuotes)) {
          cards.push({
            iconSvg: this.pendingIcon,
            title: 'Pending Quotes',
            mainValue: this.quoteData,
            clickable: true,
            isEmpty: true,
            action: () => this.goToPendingQuotes()
          });
        }
        if (this.hasPermission(Permissions.ViewAssets)) {
          cards.push({
            iconSvg: this.assetsIcon,
            title: 'Assets to be Serviced',
            mainValue: data.assets_upcoming + data.assets_past_due,
            chartData: this.assetsData,
            entityName: this.entityName_Assets,
            isEmpty: (data.assets_upcoming + data.assets_past_due) === 0
          });
        }
        if (!this.isOnlyRentee() && this.hasPermission(Permissions.ViewServiceRequests)) {
          cards.push({
            iconSvg: this.serviceRequestsIcon,
            title: 'Service Requests in Progress',
            mainValue: this.serviceRequestData,
            isEmpty: true,
            clickable: true,
            action: () => this.goToServiceRequests()
          });
        }
        if (this.hasPermission(Permissions.ViewJobs)) {
          cards.push({
            iconSvg: this.jobsIcon,
            title: 'Jobs in Progress',
            mainValue: jobs.reduce((a, b) => a + b, 0),
            chartData: this.jobsData,
            entityName: this.entityName_Jobs,
            isEmpty: jobs.every(v => v === 0)
          });
        }

        this.cards = cards;
      });
    }
  }

  hasPermission(permission: string): boolean {
    if (isQuotePermission(permission)) return hasQuotePermission(this.currentUser, permission);
    return (this.currentUser?.permission?.split(',') || []).includes(permission);
  }

  isOnlyRentee(): boolean {
    return !this.currentUser?.is_owner && !!this.currentUser?.is_rentee;
  }

  goToPendingQuotes() {
    if (this.hasPermission(Permissions.ViewQuotes)) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/quotes'], {
          queryParams: {
            status: 'Awaiting Approval'.replace(/ /g, '_').toLowerCase()
          }
        })
      );
      window.open(url, '_blank');
    }
  }

  goToServiceRequests() {
    if (this.currentUser?.permission.includes(Permissions.ViewServiceRequests)) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/service-requests'], {
          queryParams: {
            status: 'In Progress'.replace(/ /g, '_').toLowerCase()
          }
        })
      );
      window.open(url, '_blank');
    }
  }
}
